import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { useT } from "../context/LanguageContext";
import { blogCtaT } from "../translations/ui";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ASSETS = {
  logo: "/logo.png",
};

// Blog post data
const blogPosts = {
  "why-football-coaches-need-their-own-boots": {
    metaTitle: "Football Boots for Coaches — Why Players' Boots Don't Cut It",
    metaDescription: "Coaches spend 90 minutes standing on a cold, wet touchline. Here's why football boots for coaches need to be built differently — and what that looks like.",
    title: "Why Football Coaches Need Their Own Boots",
    date: "February 15, 2026",
    readTime: "5 min read",
    content: `
      <p class="lead">Every piece of kit on a football pitch is designed for the player. The boots, the socks, the shin pads — all of it built around the demands of someone covering 10 kilometres a game at high intensity.</p>
      
      <p>The coach covers about 200 metres. Slowly.</p>
      
      <p>That gap has never been addressed. Until now.</p>

      <h2>The Coach's Reality</h2>
      
      <p>You're on the touchline for 90 minutes. Sometimes longer. You're not sprinting, cutting, or accelerating — you're standing, shifting your weight, walking short distances, and reading the game.</p>
      
      <p>The conditions don't change because you're not playing. November in Denmark, a wet Saturday in Manchester, a pre-season tour in Norway — the cold and the rain are the same for you as they are for the players. The difference is, the players are moving.</p>
      
      <p>When you're stationary, your body isn't generating the heat needed to fight the cold coming up through the ground. Standard player boots — even premium ones — aren't built for that.</p>

      <h2>What Player Boots Get Wrong for Coaches</h2>
      
      <p>Player boots are engineered around movement. The ergonomics, the sole construction, the lining — all of it optimised for a player in motion.</p>
      
      <p><strong>Ergonomics:</strong> The last (the mould the boot is built on) is designed for running. When you stand still in it for 90 minutes, pressure concentrates in the wrong places. Foot fatigue builds quietly, and by the end of a double session, it's affecting your focus.</p>
      
      <p><strong>Warmth:</strong> Thin synthetic linings make sense for players generating heat through movement. They make no sense for a coach standing still in five-degree weather. After 20 minutes, you feel it. After 60, it's a problem.</p>
      
      <p><strong>Waterproofing:</strong> Most player boots offer water resistance — enough for a player in motion. Not enough for a coach standing in wet grass for 90 minutes. Water resistance slows water ingress. Waterproofing stops it.</p>

      <h2>What Football Boots for Coaches Should Do</h2>
      
      <p>The brief is straightforward. Coach football boots should:</p>
      
      <ul>
        <li>Keep your feet dry in sustained wet conditions — genuinely waterproof, not just treated</li>
        <li>Keep you warm when you're standing still in cold weather</li>
        <li>Be built on an ergonomic last designed for prolonged standing, not explosive movement</li>
      </ul>
      
      <p>That's it. It's not complicated. It just hasn't been done.</p>

      <h2>Gaffer Athletics</h2>
      
      <p>Gaffer Athletics has built the first high-end football boot designed specifically for coaches. TEX-membrane waterproofing. Warm lining. Premium calf leather. An ergonomic fit built for standing, not running.</p>
      
      <p>It launches in autumn 2026.</p>
      
      <p>Join the waitlist at <a href="/">gafferathletics.com</a>.</p>
      
      <p><strong>The best coaches prepare. Start here.</strong></p>
    `,
  },
  "the-problem-with-wearing-player-boots-as-a-coach": {
    metaTitle: "Coach Football Boots: Why Player Boots Don't Work",
    metaDescription: "Player boots are built for sprinting, not standing. Here's why coach football boots need to be different — and what that actually looks like.",
    title: "The Problem With Wearing Player Boots as a Coach",
    date: "February 15, 2026",
    readTime: "6 min read",
    content: `
      <h2>You're Wearing the Wrong Boots</h2>
      
      <p>Picture this. It's a Tuesday night in November. Training session, 7pm. The pitch is wet, the temperature is hovering around four degrees, and you've been on the touchline for 75 minutes.</p>
      
      <p>Your feet went cold around the 30-minute mark. By now, they're numb. The boots you're wearing — a decent pair, premium player boots — are doing exactly what they were designed to do. Just not for you.</p>
      
      <p>Coach football boots don't exist. Or they didn't. Every boot on the market is built for the player, not the person running the session from the side. And that gap has real consequences.</p>

      <h2>Problem 1: The Ergonomics Are Built for Running, Not Standing</h2>
      
      <p>Player boots are engineered around movement. The last (the foot-shaped mould the boot is built on) is designed to support explosive acceleration, sharp cuts, and sustained running. The pressure points, the sole stiffness, the heel-to-toe drop — all of it is optimised for a player covering 10 kilometres a game.</p>
      
      <p>You cover about 200 metres. Slowly.</p>
      
      <p>When you stand still in a boot built for running, the pressure concentrates in the wrong places. The forefoot takes load it wasn't designed to hold for 90 minutes. The result is fatigue that builds quietly — and by the end of a double session or a cup final that goes to extra time, it's affecting your focus whether you notice it or not.</p>
      
      <p>Football boots for coaches need a different ergonomic foundation. One built around standing, not sprinting.</p>

      <h2>Problem 2: The Warmth Isn't There</h2>
      
      <p>Thin synthetic linings make sense for players. You generate heat through movement, and a boot that traps too much warmth becomes a liability. Lightweight and breathable is the right call when you're working at high intensity.</p>
      
      <p>Standing still in five-degree weather is a different physiological situation entirely.</p>
      
      <p>When you're stationary, your body isn't generating the heat needed to compensate for the cold coming up through the ground. Thin linings offer almost no insulation. After 20 minutes, you feel it. After 60, it's a problem.</p>
      
      <p>A November training session, a wet Saturday morning league game, a pre-season tour in Scandinavia — these are the conditions coaches actually work in. The boot needs to handle them.</p>

      <h2>Problem 3: Water Resistance Isn't Waterproofing</h2>
      
      <p>Most premium player boots offer some degree of water resistance. It's enough for a player who's moving constantly and generating heat. It's not enough for a coach standing in wet grass for 90 minutes.</p>
      
      <p>Water resistance slows water ingress. Waterproofing stops it. That distinction matters when you're stationary on a soaked touchline. Your feet get wet, and they stay wet for the rest of the session.</p>
      
      <p>Waterproof football boots — genuinely waterproof, not just treated — are the standard coach footwear should be held to. It hasn't been, because no one has built boots specifically for coaches.</p>

      <h2>What Coach Football Boots Should Actually Do</h2>
      
      <p>The standard is straightforward. Coach football boots should keep your feet dry in sustained wet conditions. They should keep you warm when you're standing still in cold weather. And they should be built on an ergonomic last that distributes pressure evenly for prolonged standing — not explosive movement.</p>
      
      <p>That's not a complicated brief. It just hasn't been answered.</p>
      
      <p>We covered the full case for why coaches need their own boots in our first post, <a href="/blog/why-football-coaches-need-their-own-boots">Why Football Coaches Need Their Own Boots</a>. If you haven't read it, start there.</p>

      <h2>Gaffer Athletics</h2>
      
      <p>Gaffer Athletics has built the first high-end football boot designed specifically for coaches. TEX-membrane waterproofing. Warm lining. Premium calf leather. An ergonomic fit built for standing, not running.</p>
      
      <p>It launches in autumn 2026.</p>
      
      <p>Join the waitlist at <a href="/">gafferathletics.com</a>.</p>
      
      <p><strong>The right kit matters. You know that better than anyone.</strong></p>
    `,
  },
  "why-football-coaches-need-different-boots-than-players": {
    metaTitle: "Why Football Coaches Need Different Boots Than Players — The Definitive Guide",
    metaDescription: "The definitive guide to understanding why sideline footwear matters — and why The Gaffer was designed specifically for coaches, not the pitch.",
    title: "Why Football Coaches Need Different Boots Than Players",
    date: "March 24, 2026",
    readTime: "12 min read",
    content: `
      <p class="lead">The definitive guide to understanding why sideline footwear matters — and why The Gaffer was designed specifically for coaches, not the pitch.</p>

      <h2>The Problem Every Coach Knows Too Well</h2>

      <p>You've been there: standing on the touchline for 90 minutes in freezing rain, shifting your weight from foot to foot, watching your players sprint and slide while you remain still. By halftime, your toes are numb. By the final whistle, your feet are soaked, cold, and aching.</p>

      <p>Meanwhile, your players? They're warm. Their boots are designed for movement, for explosive bursts of speed that generate heat. But you're not moving like that. You're standing, pacing, occasionally jogging a few meters — and that's the fundamental difference.</p>

      <p>Football boots for players are engineered for performance on the pitch. Football boots for coaches need to be engineered for endurance on the sideline.</p>

      <h2>The Key Differences: Movement vs. Stillness</h2>

      <h3>1. Heat Generation</h3>

      <p>Players generate heat through constant movement. A midfielder covers 10-12 kilometers per match, keeping blood flowing to extremities and maintaining core temperature even in cold conditions.</p>

      <p>Coaches stand still for extended periods. Even during active coaching, you're moving a fraction of what players do. Without movement-generated warmth, your feet lose heat rapidly — especially through thin-soled football boots designed for ground feel, not insulation.</p>

      <p><strong>What coaches need:</strong> Insulation that traps warmth without bulk. The Gaffer uses natural fiber lining specifically for this — superior temperature regulation that keeps feet warm during static periods while remaining breathable during movement.</p>

      <h3>2. Water Exposure Duration</h3>

      <p>Players experience intermittent water contact. They run through puddles, slide on wet grass, but moisture doesn't penetrate deeply because they're constantly moving and their boots dry between touches.</p>

      <p>Coaches face prolonged exposure. Standing in one spot means feet sit in moisture — wet grass, puddles, rain — for the entire session. Standard football boots, designed for agility and ground contact, aren't built for extended waterproofing.</p>

      <p><strong>What coaches need:</strong> TEX-membrane technology. Not just water-resistant — genuinely waterproof for hours of continuous exposure, while maintaining breathability to prevent sweat buildup.</p>

      <h3>3. Comfort vs. Performance Trade-offs</h3>

      <p>Player boots prioritize performance: Lightweight construction, minimal padding, aggressive stud patterns for traction during acceleration and cuts. Comfort is secondary to speed and control.</p>

      <p>Coach boots prioritize comfort: You don't need a Vapor Edge to demonstrate a passing drill. You need support for prolonged standing, cushioning for all-day wear, and ergonomic design that reduces fatigue.</p>

      <p><strong>What coaches need:</strong> A heel-to-toe drop optimized for standing posture, not sprinting. The Gaffer incorporates this ergonomic principle — your heel sits slightly elevated relative to your forefoot, reducing strain on the Achilles and lower back during static positions.</p>

      <h2>Why Standard Football Boots Fail Coaches</h2>

      <h3>The "Ground Feel" Myth</h3>

      <p>Player boots are designed with minimal sole thickness to maximize ball control and pitch sensation. This is essential for a striker making split-second touches — but irrelevant for a coach holding a clipboard.</p>

      <p>That thin sole means:</p>

      <ul>
        <li>No insulation from cold ground</li>
        <li>Minimal shock absorption during prolonged standing</li>
        <li>Rapid heat loss through conduction</li>
      </ul>

      <h3>The Weight Paradox</h3>

      <p>Modern player boots weigh 150-200 grams. Manufacturers shave every gram to improve acceleration. But that weight reduction comes at a cost: structural support, padding, and insulation are eliminated.</p>

      <p>For a coach wearing boots for 10-12 hours weekly across training sessions and matches, the lack of support leads to:</p>

      <ul>
        <li>Plantar fasciitis from inadequate arch support</li>
        <li>Lower back strain from poor posture alignment</li>
        <li>Foot fatigue from minimal cushioning</li>
      </ul>

      <h3>The Durability Gap</h3>

      <p>Player boots are designed for 20-40 hours of use before replacement — a professional's seasonal cycle. They're built for performance, not longevity.</p>

      <p>Coaches need boots that last seasons, not matches. Premium calf leather (like The Gaffer uses) molds to your foot over time, becoming more comfortable with age — the opposite of synthetic player boots that degrade rapidly.</p>

      <h2>What Makes The Gaffer Different</h2>

      <h3>1. TEX Waterproof Membrane</h3>

      <p>Advanced waterproof membrane technology for prolonged sideline exposure, not just water-resistant coatings that fail after 30 minutes in heavy rain. Keeps feet completely dry through multiple training sessions in Nordic winters.</p>

      <h3>2. Natural Fiber Lining for Thermal Regulation</h3>

      <p>Premium natural fiber insulation that keeps feet warm in freezing conditions while remaining breathable during autumn sessions. Unlike synthetic linings, natural fibers wick moisture and regulate temperature without bulk.</p>

      <h3>3. Ergonomic Heel-to-Toe Drop</h3>

      <p>Designed for standing and pacing — not sprinting. Reduces strain on the Achilles tendon and lower back during static coaching positions.</p>

      <h3>4. Premium Calf Leather Construction</h3>

      <p>Built to last multiple seasons with proper care. Molds to your foot for personalized comfort — improving with age rather than degrading like synthetics.</p>

      <h3>5. Optimized for Grass, Not Pitch Performance</h3>

      <p>Stud configuration provides grip for stability on wet touchlines without the aggressive traction that causes knee strain when you're not making explosive cuts.</p>

      <h2>The Real Cost of Wrong Footwear</h2>

      <p>Using player boots for coaching isn't just uncomfortable — it's a health issue:</p>

      <ul>
        <li><strong>Cold-related injuries:</strong> Prolonged cold exposure increases injury risk to feet, ankles, and Achilles</li>
        <li><strong>Chronic fatigue:</strong> Poor support leads to compensatory strain in knees, hips, and lower back</li>
        <li><strong>Reduced coaching effectiveness:</strong> Physical discomfort distracts from tactical focus and player communication</li>
      </ul>

      <p>Professional coaches spend 15-25 hours weekly on training grounds. That's 750-1,250 hours annually. The wrong footwear compounds into chronic issues.</p>

      <h2>Who Needs Coaching Boots?</h2>

      <h3>Primary Users</h3>

      <ul>
        <li>Grassroots to professional coaches conducting outdoor sessions in variable weather</li>
        <li>Academy coaches running multiple daily sessions</li>
        <li>Player-coaches who transition between playing and coaching roles</li>
        <li>Goalkeeping coaches standing for extended periods during shot drills</li>
      </ul>

      <h3>Secondary Users</h3>

      <ul>
        <li>Referees and assistant referees requiring comfort during 90-minute matches</li>
        <li>Physios and medical staff attending training sessions on grass pitches</li>
        <li>Scouts and analysts observing matches from touchlines in all conditions</li>
      </ul>

      <h2>The Bottom Line</h2>

      <p>Player boots are optimized for 90 minutes of high-intensity movement. Coach boots need to be optimized for hours of strategic stillness in challenging conditions.</p>

      <p>The Gaffer was designed from the ground up to solve this specific problem — combining TEX waterproof membrane, natural fiber thermal regulation, ergonomic support, and premium leather durability into the first boot engineered specifically for the sideline, not the pitch.</p>

      <p>Because great coaching requires focus on your players, not your freezing feet.</p>

      <p>Ready to upgrade your sideline game? Join the waitlist for The Gaffer's autumn 2026 launch and be among the first coaches to experience purpose-built sideline footwear. <a href="/">Join waitlist →</a></p>
    `,
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts[slug];

  useEffect(() => {
    if (post) {
      document.title = post.metaTitle;
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute("content", post.metaDescription);
      }
    }
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [post]);

  const ct = useT(blogCtaT);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/blog" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Link to="/">
              <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
            </Link>
          </div>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-white/60 hover:text-white text-sm uppercase tracking-wider transition-colors">
              Home
            </Link>
            <Link to="/blog" className="text-white/60 hover:text-white text-sm uppercase tracking-wider transition-colors">
              Blog
            </Link>
          </nav>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        {/* Meta */}
        <div className="flex items-center gap-4 text-white/40 text-sm mb-6">
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {post.date}
          </span>
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {post.readTime}
          </span>
        </div>

        {/* Title */}
        <h1 className="heading-xl text-3xl md:text-4xl lg:text-5xl text-white mb-12">
          {post.title}
        </h1>

        {/* Article Content */}
        <article 
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
        />

        {/* CTA */}
        <div className="mt-16 p-8 border border-white/10 bg-white/[0.02] text-center">
          <h3 className="heading-md text-xl text-white mb-4">{ct.title}</h3>
          <p className="text-white/60 mb-6">{ct.desc}</p>
          <Link to="/" className="btn-primary inline-block">
            {ct.cta}
          </Link>
        </div>

        {/* Back to Blog */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 p-6 md:p-8 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/30 text-sm">
            © 2026 Gaffer Athletics. Engineering for the Sideline.
          </p>
        </div>
      </footer>
    </div>
  );
}
